import { Duplexer } from '@wix/duplexer-js';
import {
  DuplexerEvent,
  DuplexerSubscriber,
} from '@wix/wix-help-widget-common/duplexer';
import { Store } from 'redux';

import { TRootState } from '../common/reducers';
import { IURLChanged } from '../common/reducers/types';
import { selectWidgetSessionState } from '../common/selectors/widgetSession';
import { ACTIONS } from '../constants';
import { experiments } from '../experiments/client';
import { IInitialChatData, TUi } from '../service/InitialChatData';
import { emitter } from '../util/emitter';
import { messagesPolling } from '../util/live-chat-polling';
import { logger } from '../util/logger';
import {
  createAfter,
  createBefore,
  createMessages,
  TAction,
} from './createQueue';
import { postNewMessages } from './postNewMessages';
import { Queue } from './Queue';
import { isUrlChangedDuplexerEvent } from './utils';

export class ChatbotMessageSubscriber extends DuplexerSubscriber {
  private readonly queue: Queue;
  private _forceIgnoreMessages: boolean = false;

  constructor(
    protected readonly duplexer: Duplexer,
    protected readonly appDefId: string,
    private readonly store: Store<TRootState>,
    private readonly isWidget: boolean,
  ) {
    super(duplexer, appDefId, logger);

    this.queue = new Queue((action: TAction) => {
      store.dispatch<TAction>(action);
    });
    // TODO: this event will be part of the WidgetSessionChannel
    this.subscribe(DuplexerEvent.URL_CHANGED, this.onUrlChanged);
    this.subscribe('message', this.onMessage);
    emitter.on('message', this.onMessage);
  }

  unsubscribe() {
    super.unsubscribe();
    emitter.remove('message');
  }

  set forceIgnoreMessages(value: boolean) {
    this._forceIgnoreMessages = value;
  }

  private ignoreMessages(
    ignored: boolean,
    notHandledMessages: string[],
    ui?: TUi,
  ): boolean {
    return (
      this._forceIgnoreMessages ||
      ignored ||
      (!notHandledMessages.length && !!ui && !Object.keys(ui).length)
    );
  }

  /**
   * Receiving a message from chatbot to user.
   * Process it in our queue and post message to widget
   * @param data
   *
   */
  private onMessage = (data: IInitialChatData): void => {
    const { messages, ui, startChatData, meta, ignored, statusBanner } = data;
    const notHandledMessages = messages?.filter(
      (message) => message && !this.queue.hasHandled(message.id),
    )!;

    if (this.ignoreMessages(!!ignored, notHandledMessages, ui)) {
      return;
    }

    const notHandledMessageIds = notHandledMessages.map(({ id }) => id);
    const lastMessage = String(notHandledMessageIds.slice(-1));

    if (lastMessage) {
      messagesPolling.updateLastMessage(lastMessage);
    }

    const newMessages = createMessages(notHandledMessages, this.store);

    this.queue.addActions({
      notHandledMessageIds,
      before: createBefore(startChatData),
      after: createAfter(ui, this.store, meta, statusBanner),
      messages: newMessages,
    });
    const enableWidgetHeaderNotifications = experiments.is(
      'specs.helpwidget.enableWidgetHeaderNotifications',
      'true',
    );
    if (this.isWidget && !enableWidgetHeaderNotifications) {
      postNewMessages(newMessages, this.store, ui, meta);
    }
  };

  // TODO: move it to widget session channel (not now)
  /**
   * On navigation history (widget session) change, we update all tabs.
   * @param data
   */
  private onUrlChanged = (data: unknown): void => {
    if (!isUrlChangedDuplexerEvent(data)) {
      throw new Error(
        `Payload of DuplexerEvent.URL_CHANGED is not valid: ${JSON.stringify(
          data,
        )}`,
      );
    }
    const { sessionTabId } = selectWidgetSessionState(this.store.getState());
    if (data.id !== sessionTabId) {
      this.store.dispatch<IURLChanged>({
        type: ACTIONS.URL_CHANGED,
        ...data,
      });
    }
  };
}
